import { useState, useEffect } from 'react';
import { inject } from 'mobx-react';
import { Col, Row } from 'react-bootstrap';

//components
import withLocalization from '~/hoc/withLocalization';
import applicationRouter from '~/hoc/applicationRouter'
import MemberSearchList from '~/components/MemberSearchList/MemberSearchList.jsx';
import OvertimeBankList from './OvertimeBankList';

//elements
import LoadingSpinner from '../../../elements/LoadingSpinner.js';

const OvertimeBank = ({ overtimeBankStore, commonStore, t, userStore, transactionId }) => {
    const [overtimeBankMembers, setOvertimeBankMembers] = useState([]);
    const [currentMember, setCurrentMember] = useState(null);
    const [loading, setLoading] = useState(true);
    const currentUser = userStore?.currentUser;
    const isMember = currentUser.user_type === 'member';
    const overtimeBankSettings = commonStore.config?.client?.data?.basicRules?.overtimeBankSettings || {};
    const featureEnabled = !!overtimeBankSettings.allowOvertimeBank;

    // Member totals/balances here are always the CURRENT year's - paid overtime can only ever be
    // added against the current year (see middleware/overtimeBank.js), so this is the one balance
    // that's actually actionable, regardless of which year OvertimeBankList's own year filter is
    // browsing for historical transactions.
    useEffect(() => {
        const loadData = async () => {
            if (!featureEnabled) {
                setLoading(false);
                return;
            }
            try {
                if (isMember) {
                    const totals = await overtimeBankStore.loadEmployeeOvertimeTotals();
                    const selfMember = {
                        id: currentUser.id,
                        name: `${currentUser.first_name} ${currentUser.last_name}`,
                        image: currentUser.image,
                        internal_number: currentUser.internal_number,
                        user_type: currentUser.user_type,
                        payment_mode: currentUser.payment_mode,
                        total_hours_in_mins: totals.total_hours_in_mins,
                        paid_overtime_balance_in_mins: totals.paid_overtime_balance_in_mins,
                    };
                    setCurrentMember(selfMember);
                    overtimeBankStore.setFilter('currentUserOvertimeBankList', selfMember.id);
                    overtimeBankStore.onFilter();
                } else {
                    let pmMembers = '';
                    if (currentUser.user_type !== 'admin' && currentUser?.Manages?.length) {
                        pmMembers = currentUser.Manages.join(',');
                    }
                    const members = await overtimeBankStore.loadEmployees({ pmMembers });
                    const currMem = overtimeBankStore?.filters?.currentUserOvertimeBankList
                        ? members?.find(ele => ele?.id === overtimeBankStore?.filters?.currentUserOvertimeBankList)
                        : members[0];
                    setCurrentMember(currMem);
                    setOvertimeBankMembers(members);
                    overtimeBankStore.setFilter('currentUserOvertimeBankList', currMem?.id);
                    overtimeBankStore.onFilter();
                }
            } catch (error) {
                console.error(error);
                commonStore.addNotification(error.message || t('Something went wrong'), null, 'error');
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, []);

    const handleMemberList = (entity) => {
        setCurrentMember(entity);
        overtimeBankStore.setFilter('currentUserOvertimeBankList', entity.id);
        overtimeBankStore.onFilter();
    };

    const reloadOvertimeBalance = async () => {
        let pmMembers = '';
        if (currentUser.user_type !== 'admin' && currentUser?.Manages?.length) {
            pmMembers = currentUser.Manages.join(',');
        }
        try {
            const members = await overtimeBankStore.loadEmployees({ pmMembers });
            setOvertimeBankMembers(members);
            const updatedCurrent = members?.find(ele => ele?.id === currentMember?.id);
            if (updatedCurrent) setCurrentMember(updatedCurrent);
        } catch (error) {
            console.error(error);
            commonStore.addNotification(error.message || t('Something went wrong'), null, 'error');
        }
    };

    // Refreshes just the header stat pills for whichever member is currently selected, for the
    // newly picked year - deliberately NOT loadEmployees()/loadEmployeeOvertimeTotals() folded
    // into overtimeBankMembers/currentMemberList, since those must stay pinned to the current
    // year (see the comment on the initial load above and on the store's loadEmployeeOvertimeTotals).
    const handleYearChange = async (newYear) => {
        try {
            const totals = isMember
                ? await overtimeBankStore.loadEmployeeOvertimeTotals(newYear)
                : currentMember?.id
                    ? await overtimeBankStore.loadEmployeeOvertimeTotals(newYear, currentMember.id)
                    : null;
            if (totals) {
                setCurrentMember(prev => ({
                    ...prev,
                    total_hours_in_mins: totals.total_hours_in_mins,
                    paid_overtime_balance_in_mins: totals.paid_overtime_balance_in_mins,
                }));
            }
        } catch (error) {
            console.error(error);
            commonStore.addNotification(error.message || t('Something went wrong'), null, 'error');
        }
    };

    if (loading) return <LoadingSpinner />;

    if (!featureEnabled) {
        return (
            <div className='d-flex justify-content-center fw-600 align-items-center' style={{ minHeight: '200px' }}>
                {t('This feature is not enabled. Please contact your Administrator')}
            </div>
        );
    }

    return (
        <Row className="border border-radius-10 mt-2" style={{ maxHeight: '120vh', minHeight: '70vh', height: '120vh', overflow: 'hidden' }}>
            {!isMember &&
                <Col xs={12} md={3} className='p-0 mb-3 mb-md-0'>
                    <MemberSearchList
                        members={overtimeBankMembers}
                        selectedId={currentMember?.id}
                        onSelect={handleMemberList}
                    />
                </Col>
            }
            <Col xs={12} md={isMember ? 12 : 9}>
                {(currentMember?.id || isMember) ?
                    <OvertimeBankList
                        userDetails={currentMember}
                        isMember={isMember}
                        currentUserType={currentUser.user_type}
                        reloadOvertimeBalance={reloadOvertimeBalance}
                        transactionId={transactionId}
                        onYearChange={handleYearChange}
                    />
                    :
                    <div className='d-flex justify-content-center fw-600 align-items-center h-100'>{t('No data found')}</div>
                }
            </Col>
        </Row>
    );
};

export default inject('userStore', 'overtimeBankStore', 'commonStore')(applicationRouter(withLocalization(OvertimeBank)));